import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Image, Platform, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';
import { useDispatch } from 'react-redux';

import GiftPng from '../../../../assets/dailyMissions/gift.png';
import { RewardChipPng, RewardChipsRedPng } from '../../../../assets/event';
import { IconSendGiftSvg } from '../../../../assets/friends';
import { IconCloseSvg } from '../../../../assets/inventory';
import {
	claimReferralReward,
	getReferralOverview,
	ReferralInvitee,
	ReferralOverview,
} from '../../../../services/api';
import { goldGlow } from '../../../../shared/glowStyles';
import { profileRequest } from '../../../../store/auth/authActions';
import { useSvgId } from '../../../../utils/hooks/useSvgId';
import { IndicatorScrollView } from '../../common/ScrollIndicator';
import { Pressable } from '../../common/Touchable';
import { PANEL_WIDTH } from '../../Inventory/inventoryTheme';
import {
	BODY_PADDING,
	BODY_TOP,
	FRIENDS_FONT,
	friendsColors,
	hairline,
	inset,
	RADIUS_LG,
	RADIUS_MD,
	SECTION_GAP,
} from '../friendsTheme';
import HowItWorksOverlay from './HowItWorksOverlay';
import InvitedPlayerRow from './InvitedPlayerRow';
import { claimErrorMessage, formatChips, getInviteScreenCopy } from './inviteFriendsCatalog';
import {
	IconBack,
	IconCopy,
	IconInfo,
	IconLink,
	IconPeople,
	IconShare,
} from './inviteFriendsIcons';
import { HERO_FILL } from './inviteFriendsTheme';
import { copyToClipboard, shareInvite } from './shareInvite';
import ShareSheetOverlay from './ShareSheetOverlay';

/**
 * The Invite Friends screen from the Notion spec "Referral / Invite Friends System", opened from
 * the friends tab's invite card. It takes over the same panel the tabs live in - header, body and
 * all - so it reads as the next step of that modal rather than a second one stacked over it, and
 * Back returns to the friends list.
 *
 * Layout follows the spec's preview; the styling is the friends modal's Figma tokens. Everything
 * the player sees is the backend's (`GET /referrals`): the code and link, the milestone level, the
 * reward, the counts and the rows. Eligibility is never decided here - Claim only asks.
 */
const HEADER = {
	buttonSize: 44,
	backX: 13,
	closeX: 313,
	y: 13,
	closeIcon: 24,
	backIcon: 20,
	title: 22,
};
const CONTENT_WIDTH = 334;
const CARD_PADDING = 12;
const LIST_PAGE = 100;
const TOAST_MS = 2500;

type Props = {
	scale: number;
	panelHeight: number;
	onBack: () => void;
	onClose: () => void;
};

type LoadState =
	| { kind: 'loading' }
	| { kind: 'error' }
	| { kind: 'ready'; overview: ReferralOverview };

export const InviteFriendsPanel = ({ scale, panelHeight, onBack, onClose }: Props) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const copy = useMemo(() => getInviteScreenCopy(t), [t]);
	const styles = useMemo(() => createStyles(scale, panelHeight), [scale, panelHeight]);
	const heroId = useSvgId('inviteHero');

	const [state, setState] = useState<LoadState>({ kind: 'loading' });
	const [claimingId, setClaimingId] = useState<string | null>(null);
	const [toast, setToast] = useState<string | null>(null);
	const [howItWorksOpen, setHowItWorksOpen] = useState(false);
	const [shareSheetOpen, setShareSheetOpen] = useState(false);
	const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const showToast = useCallback((message: string) => {
		if (toastTimer.current) clearTimeout(toastTimer.current);
		setToast(message);
		toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
	}, []);

	useEffect(
		() => () => {
			if (toastTimer.current) clearTimeout(toastTimer.current);
		},
		[],
	);

	const load = useCallback(async () => {
		try {
			const overview = await getReferralOverview({ _start: 0, _end: LIST_PAGE });
			setState({ kind: 'ready', overview });
		} catch {
			setState(current => (current.kind === 'ready' ? current : { kind: 'error' }));
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const overview = state.kind === 'ready' ? state.overview : null;
	// The backend only returns a link once the casino has an invite base URL; until then the code
	// on its own is what the player shares.
	const shareTarget = overview?.referralLink ?? overview?.invitationCode ?? null;
	const rewardAmount = formatChips(overview?.program.rewardAmount ?? 0);
	const requiredLevel = overview?.program.requiredLevel ?? 5;

	const handleCopy = async () => {
		if (!shareTarget) return;
		if (await copyToClipboard(shareTarget)) {
			showToast(overview?.referralLink ? copy.linkCopied : copy.codeCopied);
		}
	};

	// Web lets the player pick the network (Facebook post, WhatsApp, ...); native hands the link
	// to the OS share sheet, which already lists every installed app.
	const handleShare = async () => {
		if (!shareTarget) return;
		if (Platform.OS === 'web') {
			setShareSheetOpen(true);
			return;
		}
		const result = await shareInvite(shareTarget);
		if (result === 'copied') showToast(copy.linkCopied);
	};

	const handleClaim = async (invitee: ReferralInvitee) => {
		if (claimingId) return;
		setClaimingId(invitee.playerId);
		try {
			const result = await claimReferralReward(invitee.playerId);
			showToast(copy.claimSuccess(formatChips(result.amount)));
			dispatch(profileRequest());
		} catch (error) {
			showToast(claimErrorMessage(t, error));
		} finally {
			setClaimingId(null);
			load();
		}
	};

	return (
		<>
			<Pressable
				accessibilityRole='button'
				accessibilityLabel={t('T_SITE_ACHIEVEMENTS_BACK', { defaultValue: 'Back' })}
				onPress={onBack}
				style={[styles.headerButton, { left: HEADER.backX * scale }]}
			>
				<IconBack size={HEADER.backIcon * scale} />
			</Pressable>
			<Text numberOfLines={1} style={styles.headerTitle}>
				{copy.title}
			</Text>
			<Pressable
				accessibilityRole='button'
				accessibilityLabel={t('T_SITE_ACHIEVEMENTS_CLOSE', { defaultValue: 'Close' })}
				onPress={onClose}
				style={[styles.headerButton, { left: HEADER.closeX * scale }]}
			>
				<View>
					<IconCloseSvg width={HEADER.closeIcon * scale} height={HEADER.closeIcon * scale} />
				</View>
			</Pressable>

			<IndicatorScrollView
				style={styles.body}
				contentContainerStyle={styles.content}
				showsVerticalScrollIndicator={false}
			>
				{/* Hero: the pitch on the left, the per-friend reward badge and artwork on the right. */}
				<View style={styles.hero}>
					<View pointerEvents='none' style={styles.heroPlate}>
						<Svg width='100%' height='100%'>
							<Defs>
								<LinearGradient id={heroId} x1='0' y1='0' x2='1' y2='1'>
									{HERO_FILL.map(([offset, color]) => (
										<Stop key={offset} offset={offset} stopColor={color} />
									))}
								</LinearGradient>
							</Defs>
							<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${heroId})`} />
						</Svg>
					</View>
					<View style={styles.heroCopy}>
						<Text style={styles.heroTitle}>{copy.heroTitle}</Text>
						<Text style={styles.heroBody}>{copy.heroBody(requiredLevel, rewardAmount)}</Text>
					</View>
					<View style={styles.heroReward}>
						<View style={styles.rewardBadge}>
							<Text style={styles.rewardBadgeLabel}>{copy.rewardBadge}</Text>
							<View style={styles.rewardBadgeValue}>
								<Image source={RewardChipPng} style={styles.rewardChip} />
								<Text numberOfLines={1} style={styles.rewardBadgeText}>
									{copy.rewardChips(rewardAmount)}
								</Text>
							</View>
						</View>
						<View style={styles.heroArt}>
							<Image source={RewardChipsRedPng} style={styles.heroChips} resizeMode='contain' />
							<Image source={GiftPng} style={styles.heroGift} resizeMode='contain' />
						</View>
					</View>
				</View>

				{/* Your Invite Link */}
				<View style={styles.card}>
					<View style={styles.cardTitleRow}>
						<IconLink size={18 * scale} />
						<Text style={styles.cardTitle}>
							{overview && !overview.referralLink ? copy.codeTitle : copy.linkTitle}
						</Text>
					</View>
					<View style={styles.linkRow}>
						<View style={styles.linkField}>
							<Text numberOfLines={1} selectable style={styles.linkText}>
								{shareTarget ?? '…'}
							</Text>
						</View>
						<Pressable
							accessibilityRole='button'
							accessibilityLabel={t('T_SITE_A11Y_COPY_LINK', { defaultValue: 'Copy link' })}
							disabled={!shareTarget}
							onPress={handleCopy}
							style={styles.iconButton}
						>
							<IconCopy size={18 * scale} />
						</Pressable>
					</View>
					<View style={styles.actionRow}>
						<Pressable
							accessibilityRole='button'
							disabled={!shareTarget}
							onPress={handleShare}
							style={[styles.actionButton, styles.shareButton]}
						>
							<IconShare size={16 * scale} />
							<Text numberOfLines={1} style={styles.actionLabel}>
								{copy.share}
							</Text>
						</Pressable>
						<Pressable
							accessibilityRole='button'
							onPress={() => setHowItWorksOpen(true)}
							style={styles.actionButton}
						>
							<IconInfo size={16 * scale} />
							<Text numberOfLines={1} style={styles.actionLabel}>
								{copy.howItWorks}
							</Text>
						</Pressable>
					</View>
				</View>

				{/* Statistics */}
				<View style={[styles.card, styles.stats]}>
					<View style={styles.stat}>
						<IconPeople size={30 * scale} />
						<View>
							<Text style={styles.statLabel}>{copy.invitedPlayers}</Text>
							<Text style={styles.statValue}>{overview?.stats.invitedCount ?? '–'}</Text>
						</View>
					</View>
					<View style={styles.statDivider} />
					<View style={styles.stat}>
						<IconSendGiftSvg width={24 * scale} height={27 * scale} />
						<View>
							<Text style={styles.statLabel}>{copy.rewardsClaimed}</Text>
							<Text style={styles.statValue}>
								{overview ? `${overview.stats.claimedCount} / ${overview.stats.invitedCount}` : '–'}
							</Text>
						</View>
					</View>
				</View>

				<Text style={styles.listTitle}>{copy.listTitle}</Text>

				{state.kind === 'loading' ? (
					<ActivityIndicator color={friendsColors.accent} style={styles.placeholder} />
				) : state.kind === 'error' ? (
					<Pressable accessibilityRole='button' onPress={load} style={styles.placeholder}>
						<Text style={styles.placeholderText}>{copy.loadError}</Text>
						<Text style={[styles.placeholderText, styles.retry]}>{copy.retry}</Text>
					</Pressable>
				) : (
					<>
						{!state.overview.program.isEnabled ? (
							<Text style={styles.placeholderText}>{copy.disabled}</Text>
						) : null}
						{state.overview.items.length === 0 ? (
							<Text style={[styles.placeholderText, styles.placeholder]}>{copy.empty}</Text>
						) : (
							<View style={styles.list}>
								{state.overview.items.map(invitee => (
									<InvitedPlayerRow
										key={invitee.playerId}
										invitee={invitee}
										scale={scale}
										claiming={claimingId === invitee.playerId}
										onClaim={handleClaim}
									/>
								))}
							</View>
						)}
					</>
				)}
			</IndicatorScrollView>

			{toast ? (
				<View pointerEvents='none' style={styles.toast}>
					<Text style={styles.toastText}>{toast}</Text>
				</View>
			) : null}

			{shareSheetOpen && shareTarget ? (
				<ShareSheetOverlay
					scale={scale}
					link={shareTarget}
					onCopy={handleCopy}
					onClose={() => setShareSheetOpen(false)}
				/>
			) : null}

			{howItWorksOpen ? (
				<HowItWorksOverlay
					scale={scale}
					requiredLevel={requiredLevel}
					onClose={() => setHowItWorksOpen(false)}
				/>
			) : null}
		</>
	);
};

const createStyles = (scale: number, panelHeight: number) => {
	const cardBase = {
		width: CONTENT_WIDTH * scale,
		padding: inset(CARD_PADDING, scale),
		borderRadius: RADIUS_LG * scale,
		borderWidth: hairline(scale),
		borderColor: friendsColors.border,
		backgroundColor: friendsColors.surface,
	} as const;
	const text = (size: number, weight: '500' | '700' | '900', color: string, lineHeight = 1.3) =>
		({
			color,
			fontFamily: FRIENDS_FONT,
			fontSize: size * scale,
			fontWeight: weight,
			lineHeight: size * lineHeight * scale,
		}) as const;

	return StyleSheet.create({
		headerButton: {
			position: 'absolute',
			top: HEADER.y * scale,
			width: HEADER.buttonSize * scale,
			height: HEADER.buttonSize * scale,
			alignItems: 'center',
			justifyContent: 'center',
			zIndex: 2,
		},
		headerTitle: {
			position: 'absolute',
			left: 0,
			right: 0,
			top: (HEADER.y + (HEADER.buttonSize - HEADER.title * 1.3) / 2) * scale,
			textAlign: 'center',
			...text(HEADER.title, '700', friendsColors.gold50),
		},
		body: {
			position: 'absolute',
			left: 0,
			top: BODY_TOP * scale,
			width: PANEL_WIDTH * scale,
			height: (panelHeight - BODY_TOP) * scale,
		},
		content: {
			paddingHorizontal: BODY_PADDING * scale,
			paddingTop: BODY_PADDING * scale,
			paddingBottom: 24 * scale,
			gap: SECTION_GAP * scale,
		},
		hero: {
			...cardBase,
			flexDirection: 'row',
			gap: 8 * scale,
			overflow: 'hidden',
		},
		heroPlate: {
			...StyleSheet.absoluteFillObject,
			borderRadius: RADIUS_LG * scale,
			overflow: 'hidden',
		},
		heroCopy: {
			flex: 1.3,
			gap: 6 * scale,
		},
		heroTitle: text(20, '900', friendsColors.accent, 1.15),
		heroBody: text(12, '500', friendsColors.gold50, 1.4),
		heroReward: {
			flex: 1,
			alignItems: 'center',
			gap: 6 * scale,
		},
		rewardBadge: {
			alignItems: 'center',
			paddingHorizontal: 6 * scale,
			paddingVertical: 4 * scale,
			borderRadius: RADIUS_MD * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.borderGold,
			backgroundColor: friendsColors.surfaceSunken,
			gap: 2 * scale,
		},
		rewardBadgeLabel: text(8, '900', friendsColors.gold300, 1.2),
		rewardBadgeValue: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: 4 * scale,
		},
		rewardChip: {
			width: 14 * scale,
			height: 14 * scale,
		},
		rewardBadgeText: text(11, '900', friendsColors.gold50, 1.2),
		heroArt: {
			width: 110 * scale,
			height: 70 * scale,
		},
		heroChips: {
			position: 'absolute',
			left: 0,
			bottom: 0,
			width: 72 * scale,
			height: 40 * scale,
		},
		heroGift: {
			position: 'absolute',
			right: 0,
			bottom: 0,
			width: 66 * scale,
			height: 66 * scale,
		},
		card: {
			...cardBase,
			gap: 10 * scale,
		},
		cardTitleRow: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: 8 * scale,
		},
		cardTitle: text(16, '700', friendsColors.gold50),
		linkRow: {
			flexDirection: 'row',
			gap: 8 * scale,
		},
		linkField: {
			flex: 1,
			height: 40 * scale,
			justifyContent: 'center',
			paddingHorizontal: inset(12, scale),
			borderRadius: RADIUS_MD * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.border,
			backgroundColor: friendsColors.surfaceSunken,
		},
		linkText: text(13, '500', friendsColors.primary),
		iconButton: {
			width: 40 * scale,
			height: 40 * scale,
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: RADIUS_MD * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.borderGold,
			backgroundColor: friendsColors.surfaceSunken,
			...goldGlow,
		},
		actionRow: {
			flexDirection: 'row',
			gap: 8 * scale,
		},
		actionButton: {
			flex: 1,
			height: 40 * scale,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			gap: 6 * scale,
			paddingHorizontal: 6 * scale,
			borderRadius: RADIUS_MD * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.border,
			backgroundColor: friendsColors.surfaceSunken,
		},
		shareButton: {
			flex: 1.35,
			borderColor: friendsColors.borderGold,
			...goldGlow,
		},
		actionLabel: {
			flexShrink: 1,
			...text(13, '700', friendsColors.gold50, 1.2),
		},
		stats: {
			flexDirection: 'row',
			alignItems: 'center',
		},
		stat: {
			flex: 1,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			gap: 10 * scale,
		},
		statDivider: {
			width: hairline(scale),
			alignSelf: 'stretch',
			backgroundColor: friendsColors.border,
		},
		statLabel: text(12, '500', friendsColors.gold50),
		statValue: text(18, '900', friendsColors.accent, 1.2),
		listTitle: text(18, '700', friendsColors.accent),
		list: {
			gap: 8 * scale,
		},
		placeholder: {
			paddingVertical: 24 * scale,
			alignItems: 'center',
		},
		placeholderText: {
			textAlign: 'center',
			...text(13, '500', friendsColors.muted, 1.4),
		},
		retry: {
			marginTop: 6 * scale,
			color: friendsColors.accent,
			fontWeight: '700',
		},
		toast: {
			position: 'absolute',
			left: 24 * scale,
			right: 24 * scale,
			bottom: 24 * scale,
			paddingVertical: 10 * scale,
			paddingHorizontal: 14 * scale,
			borderRadius: RADIUS_MD * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.borderGold,
			backgroundColor: friendsColors.surfaceSunken,
			zIndex: 3,
		},
		toastText: {
			textAlign: 'center',
			...text(13, '700', friendsColors.gold50),
		},
	});
};

export default InviteFriendsPanel;
